import React, { useState, useEffect } from 'react';
import { reportsAPI, orderAPI, menuAPI, inventoryAPI } from '../services/api';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { FileText, TrendingUp, Calendar, BarChart3 } from 'lucide-react';

export default function Reports() {
  const [reportType, setReportType] = useState('performance');
  const [loading, setLoading] = useState(false);
  const [performanceData, setPerformanceData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [itemAnalysis, setItemAnalysis] = useState(null);
  const [menuItems, setMenuItems] = useState({});
  const [profitLossData, setProfitLossData] = useState(null);
  
  // Date filter states
  const [dateFilter, setDateFilter] = useState('month'); // today, week, month, custom
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Helper function to get date range
  const getDateRange = () => {
    const now = new Date();
    let startDate, endDate;
    
    switch (dateFilter) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'week':
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay());
        startDate = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate());
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59);
        }
        break;
      default:
        return null;
    }
    
    if (startDate && endDate) {
      return {
        start: startDate.toISOString().split('T')[0],
        end: endDate.toISOString().split('T')[0]
      };
    }
    return null;
  };

  useEffect(() => {
    loadMenuItems();
  }, []);

  useEffect(() => {
    loadReports();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, dateFilter, customStartDate, customEndDate]);

  const loadMenuItems = async () => {
    try {
      const response = await menuAPI.getAll();
      if (response.data.success) {
        const itemMap = {};
        response.data.data.forEach(item => {
          itemMap[item._id] = item;
        });
        setMenuItems(itemMap);
      }
    } catch (err) {
      console.error('Failed to fetch menu items:', err);
    }
  };

  const loadReports = async () => {
    try {
      setLoading(true);
      
      if (reportType === 'performance') {
        await loadPerformanceData();
      } else if (reportType === 'monthly') {
        await loadMonthlyData();
      } else if (reportType === 'items') {
        await loadItemAnalysis();
      } else if (reportType === 'profitloss') {
        await loadProfitLossData();
      }
    } catch (err) {
      console.error('Failed to load reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPerformanceData = async () => {
    try {
      const response = await reportsAPI.getSummary();
      if (response.data.success) {
        setPerformanceData(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch performance data', err);
    }
  };

  const loadMonthlyData = async () => {
    try {
      const dateRange = getDateRange();
      const allOrders = await orderAPI.getAll(dateRange?.start, dateRange?.end);
      if (allOrders.data.success) {
        const orders = allOrders.data.data;
        const monthlyStats = calculateMonthlyStats(orders);
        setMonthlyData(monthlyStats);
      }
    } catch (err) {
      console.error('Failed to fetch monthly data', err);
    }
  };

  const loadItemAnalysis = async () => {
    try {
      const dateRange = getDateRange();
      const response = await orderAPI.getAll(dateRange?.start, dateRange?.end);
      if (response.data.success) {
        const analysis = performABCAnalysis(response.data.data);
        setItemAnalysis(analysis);
      }
    } catch (err) {
      console.error('Failed to fetch item analysis', err);
    }
  };

  const loadProfitLossData = async () => {
    try {
      const dateRange = getDateRange();
      const [ordersResponse, inventoryResponse, menuResponse] = await Promise.all([
        orderAPI.getAll(dateRange?.start, dateRange?.end),
        inventoryAPI.getAll(),
        menuAPI.getAll()
      ]);

      if (ordersResponse.data.success && inventoryResponse.data.success && menuResponse.data.success) {
        const orders = ordersResponse.data.data;
        const inventory = inventoryResponse.data.data;
        const menu = menuResponse.data.data;

        // Create maps for easy lookup
        const inventoryMap = {};
        inventory.forEach(item => {
          inventoryMap[item._id] = item;
        });

        const menuMap = {};
        menu.forEach(item => {
          menuMap[item._id] = item;
        });

        // Initialize itemBreakdown with ALL inventory items
        const itemBreakdown = {};
        inventory.forEach(invItem => {
          const menuItem = menuMap[invItem._id];
          
          // Calculate cost for unsold inventory
          let unsoldCost = 0;
          let costPerUnit = 0;
          
          if (invItem.cost_per_unit && invItem.cost_per_unit > 0) {
            costPerUnit = invItem.cost_per_unit;
            unsoldCost = (invItem.stock || 0) * costPerUnit;
          } else if (menuItem?.price) {
            // Use estimated cost (35% of price)
            costPerUnit = menuItem.price * 0.35;
            unsoldCost = (invItem.stock || 0) * costPerUnit;
          }
          
          itemBreakdown[invItem._id] = {
            name: menuItem?.name || invItem.name || 'Unknown Item',
            quantitySold: 0,
            revenue: 0,
            cost: 0,
            profit: 0,
            currentStock: invItem.stock || 0,
            unsoldCost: unsoldCost,
            costPerUnit: costPerUnit,
            hasCostData: false,
            calculationMethod: 'none'
          };
        });

        // Calculate total quantity sold per item
        const itemSalesMap = {};
        orders.forEach(order => {
          order.items.forEach(orderItem => {
            if (!itemSalesMap[orderItem.item_id]) {
              itemSalesMap[orderItem.item_id] = 0;
            }
            itemSalesMap[orderItem.item_id] += orderItem.quantity || 0;
          });
        });

        // Calculate cost based on stock vs orders ratio
        let totalRevenue = 0;
        let totalCost = 0;

        orders.forEach(order => {
          totalRevenue += order.total_amount || 0;

          order.items.forEach(orderItem => {
            const invItem = inventoryMap[orderItem.item_id];
            const menuItem = menuMap[orderItem.item_id];
            const itemName = menuItem?.name || 'Unknown Item';

            // Initialize item if not in breakdown (items not in inventory)
            if (!itemBreakdown[orderItem.item_id]) {
              itemBreakdown[orderItem.item_id] = {
                name: itemName,
                quantitySold: 0,
                revenue: 0,
                cost: 0,
                profit: 0,
                currentStock: 0,
                unsoldCost: 0,
                costPerUnit: 0,
                hasCostData: false,
                calculationMethod: 'none'
              };
            }

            // Calculate cost based on stock percentage used
            let costPerUnit = 0;
            let calculationMethod = 'none';

            if (invItem) {
              // Method 1: If cost_per_unit is available, use it
              if (invItem.cost_per_unit && invItem.cost_per_unit > 0) {
                costPerUnit = invItem.cost_per_unit;
                calculationMethod = 'cost_per_unit';
              }
              // Method 2: Calculate based on total stock and quantity sold
              else if (invItem.stock !== undefined && itemSalesMap[orderItem.item_id]) {
                // Total stock = current stock + quantity sold
                const totalStock = (invItem.stock || 0) + itemSalesMap[orderItem.item_id];
                
                if (totalStock > 0 && menuItem?.price) {
                  // Estimate cost as percentage of selling price based on typical restaurant margins
                  // Assuming 30-40% food cost is standard in restaurants
                  const estimatedCostPercentage = 0.35; // 35% of selling price
                  costPerUnit = menuItem.price * estimatedCostPercentage;
                  calculationMethod = 'estimated';
                }
              }
            }

            const itemRevenue = (orderItem.quantity || 0) * (orderItem.price || 0);
            const itemCost = (orderItem.quantity || 0) * costPerUnit;

            itemBreakdown[orderItem.item_id].quantitySold += orderItem.quantity || 0;
            itemBreakdown[orderItem.item_id].revenue += itemRevenue;
            itemBreakdown[orderItem.item_id].cost += itemCost;
            itemBreakdown[orderItem.item_id].profit += (itemRevenue - itemCost);
            itemBreakdown[orderItem.item_id].hasCostData = calculationMethod !== 'none';
            itemBreakdown[orderItem.item_id].calculationMethod = calculationMethod;

            totalCost += itemCost;
          });
        });

        const totalProfit = totalRevenue - totalCost;
        const profitMargin = totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100) : 0;

        // Calculate total unsold inventory cost
        const totalUnsoldCost = Object.values(itemBreakdown).reduce((sum, item) => {
          return sum + (item.quantitySold === 0 ? item.unsoldCost : 0);
        }, 0);

        // Sort items by profit (sold items first, then by profit, then unsold items)
        const itemBreakdownArray = Object.values(itemBreakdown)
          .map(item => ({
            ...item,
            revenue: parseFloat(item.revenue.toFixed(2)),
            cost: parseFloat(item.cost.toFixed(2)),
            profit: parseFloat(item.profit.toFixed(2)),
            margin: item.revenue > 0 ? parseFloat(((item.profit / item.revenue) * 100).toFixed(2)) : 0,
            unsoldCost: parseFloat(item.unsoldCost.toFixed(2)),
            costPerUnit: parseFloat(item.costPerUnit.toFixed(2))
          }))
          .sort((a, b) => {
            // First, sort by whether item was sold (sold items first)
            if (a.quantitySold > 0 && b.quantitySold === 0) return -1;
            if (a.quantitySold === 0 && b.quantitySold > 0) return 1;
            // Then sort by profit
            return b.profit - a.profit;
          });

        // Check calculation methods
        const itemsWithActualCost = itemBreakdownArray.filter(item => item.calculationMethod === 'cost_per_unit').length;
        const itemsWithEstimatedCost = itemBreakdownArray.filter(item => item.calculationMethod === 'estimated').length;
        const itemsWithNoCost = itemBreakdownArray.filter(item => item.calculationMethod === 'none').length;
        const totalItems = itemBreakdownArray.length;
        const soldItems = itemBreakdownArray.filter(item => item.quantitySold > 0).length;
        const unsoldItems = itemBreakdownArray.filter(item => item.quantitySold === 0).length;

        setProfitLossData({
          totalRevenue: parseFloat(totalRevenue.toFixed(2)),
          totalCost: parseFloat(totalCost.toFixed(2)),
          totalProfit: parseFloat(totalProfit.toFixed(2)),
          profitMargin: parseFloat(profitMargin.toFixed(2)),
          totalUnsoldCost: parseFloat(totalUnsoldCost.toFixed(2)),
          itemBreakdown: itemBreakdownArray,
          topProfitable: itemBreakdownArray.filter(item => item.quantitySold > 0).slice(0, 10),
          leastProfitable: itemBreakdownArray.filter(item => item.quantitySold > 0).slice(-10).reverse(),
          hasCostData: (itemsWithActualCost + itemsWithEstimatedCost) > 0,
          costDataPercentage: totalItems > 0 ? (((itemsWithActualCost + itemsWithEstimatedCost) / totalItems) * 100).toFixed(0) : 0,
          itemsWithActualCost,
          itemsWithEstimatedCost,
          itemsWithNoCost,
          soldItems,
          unsoldItems,
          totalItems
        });
      }
    } catch (err) {
      console.error('Failed to fetch profit/loss data', err);
      setProfitLossData({
        totalRevenue: 0,
        totalCost: 0,
        totalProfit: 0,
        profitMargin: 0,
        totalUnsoldCost: 0,
        itemBreakdown: [],
        topProfitable: [],
        leastProfitable: [],
        hasCostData: false,
        costDataPercentage: 0,
        itemsWithActualCost: 0,
        itemsWithEstimatedCost: 0,
        itemsWithNoCost: 0,
        soldItems: 0,
        unsoldItems: 0,
        totalItems: 0,
        error: 'Failed to load data'
      });
    }
  };

  const calculateMonthlyStats = (orders) => {
    const monthlyData = {};
    
    orders.forEach(order => {
      const date = new Date(order.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { 
          month: new Date(date.getFullYear(), date.getMonth()).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          sales: 0, 
          orders: 0,
          averageOrder: 0
        };
      }
      
      monthlyData[monthKey].sales += order.total_amount;
      monthlyData[monthKey].orders += 1;
    });

    Object.values(monthlyData).forEach(month => {
      month.averageOrder = month.orders > 0 ? (month.sales / month.orders).toFixed(2) : 0;
      month.sales = parseFloat(month.sales.toFixed(2));
    });

    return Object.values(monthlyData).sort((a, b) => 
      new Date(a.month) - new Date(b.month)
    ).slice(-12);
  };

  const performABCAnalysis = (orders) => {
    const itemStats = {};
    
    orders.forEach(order => {
      order.items.forEach(item => {
        if (!itemStats[item.item_id]) {
          // Get the actual item name from menuItems
          const menuItem = menuItems[item.item_id];
          itemStats[item.item_id] = {
            name: menuItem?.name || item.item_name || `Unknown Item`,
            quantity: 0,
            revenue: 0
          };
        }
        itemStats[item.item_id].quantity += item.quantity;
        itemStats[item.item_id].revenue += item.quantity * item.price;
      });
    });

    const items = Object.values(itemStats)
      .sort((a, b) => b.revenue - a.revenue)
      .map(item => ({
        ...item,
        revenue: parseFloat(item.revenue.toFixed(2))
      }));

    const totalRevenue = items.reduce((sum, item) => sum + item.revenue, 0);
    let cumulativeRevenue = 0;
    
    const classified = items.map(item => {
      cumulativeRevenue += item.revenue;
      const percentage = (cumulativeRevenue / totalRevenue) * 100;
      
      let classification = 'C';
      if (percentage <= 80) {
        classification = 'A';
      } else if (percentage <= 95) {
        classification = 'B';
      }
      
      return {
        ...item,
        classification,
        percentage: parseFloat(((item.revenue / totalRevenue) * 100).toFixed(2))
      };
    });

    const abc = {
      A: classified.filter(item => item.classification === 'A'),
      B: classified.filter(item => item.classification === 'B'),
      C: classified.filter(item => item.classification === 'C'),
      all: classified,
      summary: {
        A: { count: 0, revenue: 0, percentage: 0 },
        B: { count: 0, revenue: 0, percentage: 0 },
        C: { count: 0, revenue: 0, percentage: 0 }
      }
    };

    Object.keys(abc).slice(0, 3).forEach(key => {
      abc.summary[key].count = abc[key].length;
      abc.summary[key].revenue = abc[key].reduce((sum, item) => sum + item.revenue, 0);
      abc.summary[key].percentage = (abc.summary[key].revenue / totalRevenue * 100).toFixed(2);
    });

    return abc;
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="text-center py-12">
          <p className="text-lg text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FileText className="h-8 w-8 text-orange-600" />
            Reports & Analytics
          </h1>
          <p className="text-gray-500 text-sm mt-1">Business intelligence and performance analysis</p>
        </div>

        {/* Report Type Selector */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <Button
            onClick={() => setReportType('performance')}
            variant={reportType === 'performance' ? 'default' : 'outline'}
            className="gap-2"
          >
            <TrendingUp className="h-4 w-4" />
            Performance
          </Button>
          <Button
            onClick={() => setReportType('monthly')}
            variant={reportType === 'monthly' ? 'default' : 'outline'}
            className="gap-2"
          >
            <Calendar className="h-4 w-4" />
            Monthly Trends
          </Button>
          <Button
            onClick={() => setReportType('items')}
            variant={reportType === 'items' ? 'default' : 'outline'}
            className="gap-2"
          >
            <BarChart3 className="h-4 w-4" />
            Item Analysis
          </Button>
          <Button
            onClick={() => setReportType('profitloss')}
            variant={reportType === 'profitloss' ? 'default' : 'outline'}
            className="gap-2"
          >
            <FileText className="h-4 w-4" />
            Profit & Loss
          </Button>
        </div>

        {/* Date Range Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="h-5 w-5 text-orange-600" />
              <span className="text-sm font-semibold text-gray-700">Date Range:</span>
            </div>
            <div className="flex gap-2 flex-wrap mb-4">
              {['today', 'week', 'month', 'custom'].map((filter) => (
                <Button
                  key={filter}
                  onClick={() => setDateFilter(filter)}
                  variant={dateFilter === filter ? 'default' : 'outline'}
                  size="sm"
                  className="capitalize"
                >
                  {filter === 'today' ? 'Today' : filter === 'week' ? 'This Week' : filter === 'month' ? 'This Month' : 'Custom Range'}
                </Button>
              ))}
            </div>
            
            {/* Custom Date Range Inputs */}
            {dateFilter === 'custom' && (
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label htmlFor="start-date">From:</Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-date">To:</Label>
                  <Input
                    id="end-date"
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* PERFORMANCE REPORT */}
        {reportType === 'performance' && performanceData && (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-blue-600">
                <p className="text-gray-600 text-xs font-medium">Total Sales</p>
                <p className="text-2xl font-bold text-blue-600 mt-2">₹{performanceData.total_sales.toFixed(2)}</p>
                <p className="text-xs text-gray-500 mt-1">All-time</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-green-600">
                <p className="text-gray-600 text-xs font-medium">Total Orders</p>
                <p className="text-2xl font-bold text-green-600 mt-2">{performanceData.total_orders}</p>
                <p className="text-xs text-gray-500 mt-1">All orders</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-purple-600">
                <p className="text-gray-600 text-xs font-medium">Avg Order Value</p>
                <p className="text-2xl font-bold text-purple-600 mt-2">
                  ₹{(performanceData.total_sales / (performanceData.total_orders || 1)).toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Per order</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-orange-600">
                <p className="text-gray-600 text-xs font-medium">Ready Orders</p>
                <p className="text-2xl font-bold text-orange-600 mt-2">
                  {performanceData.status_breakdown?.ready || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">Prepared</p>
              </div>
            </div>

            {/* Status Breakdown Chart */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Order Status Distribution</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Preparing', value: performanceData.status_breakdown?.preparing || 0, fill: '#2563eb' },
                      { name: 'Ready', value: performanceData.status_breakdown?.ready || 0, fill: '#16a34a' }
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    <Cell fill="#2563eb" />
                    <Cell fill="#16a34a" />
                  </Pie>
                  <Tooltip formatter={(value) => `${value} orders`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* MONTHLY TRENDS REPORT */}
        {reportType === 'monthly' && monthlyData && monthlyData.length > 0 && (
          <div className="space-y-6">
            {/* Sales Trend Chart */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">📊 Monthly Sales Trend</h2>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={monthlyData} margin={{ top: 20, right: 30, left: 0, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis 
                    dataKey="month" 
                    angle={-45} 
                    textAnchor="end" 
                    height={100}
                    tick={{ fill: '#666', fontSize: 12 }}
                  />
                  <YAxis tick={{ fill: '#666', fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => [
                      typeof value === 'number' ? `₹${value.toFixed(2)}` : value,
                      ''
                    ]}
                    contentStyle={{ backgroundColor: '#fff', border: '2px solid #FF6B35' }}
                  />
                  <Legend />
                  <Bar dataKey="sales" fill="#FF6B35" name="Total Sales (₹)" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Orders & Average Order Value */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Monthly Orders</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fill: '#666', fontSize: 11 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis tick={{ fill: '#666', fontSize: 12 }} />
                    <Tooltip contentStyle={{ backgroundColor: '#fff', border: '2px solid #004E89' }} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="orders" 
                      stroke="#004E89" 
                      strokeWidth={3}
                      dot={{ fill: '#004E89', r: 6 }}
                      activeDot={{ r: 8 }}
                      name="Order Count"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Average Order Value</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis 
                      dataKey="month"
                      tick={{ fill: '#666', fontSize: 11 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis 
                      tick={{ fill: '#666', fontSize: 12 }}
                      tickFormatter={(value) => `₹${value}`}
                    />
                    <Tooltip 
                      formatter={(value) => `₹${value}`}
                      contentStyle={{ backgroundColor: '#fff', border: '2px solid #06A77D' }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="averageOrder" 
                      stroke="#06A77D" 
                      strokeWidth={3}
                      dot={{ fill: '#06A77D', r: 6 }}
                      activeDot={{ r: 8 }}
                      name="Avg Order Value (₹)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Monthly Summary Table */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">📋 Monthly Summary</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-orange-100 to-amber-100 border-b-2 border-orange-300">
                      <th className="px-4 py-3 text-left text-gray-800 font-bold">Month</th>
                      <th className="px-4 py-3 text-right text-gray-800 font-bold">Orders</th>
                      <th className="px-4 py-3 text-right text-gray-800 font-bold">Total Sales</th>
                      <th className="px-4 py-3 text-right text-gray-800 font-bold">Avg Order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyData.map((month, idx) => (
                      <tr key={idx} className="border-b hover:bg-orange-50 transition">
                        <td className="px-4 py-3 text-gray-800 font-semibold">{month.month}</td>
                        <td className="px-4 py-3 text-right text-gray-700">{month.orders}</td>
                        <td className="px-4 py-3 text-right text-orange-600 font-bold">₹{month.sales.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right text-green-600 font-bold">₹{month.averageOrder}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ABC ANALYSIS REPORT */}
        {reportType === 'items' && itemAnalysis && (
          <div className="space-y-6">
            {/* ABC Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-red-600">
                <p className="text-gray-600 text-xs font-medium uppercase">Category A - Fast Moving</p>
                <p className="text-xl font-bold text-red-600 mt-2">{itemAnalysis.summary.A.count} Items</p>
                <p className="text-lg text-red-500 font-semibold">₹{itemAnalysis.summary.A.revenue.toFixed(2)}</p>
                <p className="text-xs text-gray-600 mt-2">{itemAnalysis.summary.A.percentage}% of revenue</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-yellow-600">
                <p className="text-gray-600 text-xs font-medium uppercase">Category B - Moderate</p>
                <p className="text-xl font-bold text-yellow-600 mt-2">{itemAnalysis.summary.B.count} Items</p>
                <p className="text-lg text-yellow-500 font-semibold">₹{itemAnalysis.summary.B.revenue.toFixed(2)}</p>
                <p className="text-xs text-gray-600 mt-2">{itemAnalysis.summary.B.percentage}% of revenue</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-green-600">
                <p className="text-gray-600 text-xs font-medium uppercase">Category C - Slow Moving</p>
                <p className="text-xl font-bold text-green-600 mt-2">{itemAnalysis.summary.C.count} Items</p>
                <p className="text-lg text-green-500 font-semibold">₹{itemAnalysis.summary.C.revenue.toFixed(2)}</p>
                <p className="text-xs text-gray-600 mt-2">{itemAnalysis.summary.C.percentage}% of revenue</p>
              </div>
            </div>

            {/* ABC Distribution Chart */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Item Classification Distribution</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: `A - Fast Moving (${itemAnalysis.summary.A.count})`, value: itemAnalysis.summary.A.revenue },
                      { name: `B - Moderate (${itemAnalysis.summary.B.count})`, value: itemAnalysis.summary.B.revenue },
                      { name: `C - Slow Moving (${itemAnalysis.summary.C.count})`, value: itemAnalysis.summary.C.revenue }
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    <Cell fill="#FF6B35" />
                    <Cell fill="#004E89" />
                    <Cell fill="#06A77D" />
                  </Pie>
                  <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Top Items by Category */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Category A */}
              <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200">
                <h3 className="text-xl font-bold text-orange-600 mb-4">
                  🔥 Top Fast-Moving Items (Category A)
                </h3>
                <div className="space-y-3">
                  {itemAnalysis.A.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="p-3 bg-orange-50 rounded-lg border-l-4 border-orange-600">
                      <p className="font-bold text-gray-800">{item.name}</p>
                      <div className="flex justify-between mt-1">
                        <span className="text-sm text-gray-600">Qty: {item.quantity}</span>
                        <span className="text-sm font-bold text-orange-600">₹{item.revenue.toFixed(2)}</span>
                      </div>
                      <div className="w-full bg-orange-200 rounded-full h-2 mt-2">
                        <div 
                          className="bg-orange-600 h-2 rounded-full" 
                          style={{ width: `${item.percentage}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{item.percentage}% of total revenue</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Category B */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-blue-600 mb-4">
                  📊 Moderate-Moving Items (Category B)
                </h3>
                <div className="space-y-3">
                  {itemAnalysis.B.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-600">
                      <p className="font-bold text-gray-800">{item.name}</p>
                      <div className="flex justify-between mt-1">
                        <span className="text-sm text-gray-600">Qty: {item.quantity}</span>
                        <span className="text-sm font-bold text-blue-600">₹{item.revenue.toFixed(2)}</span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${Math.min(item.percentage, 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{item.percentage}% of total revenue</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Category C */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-green-600 mb-4">
                  🐢 Slow-Moving Items (Category C)
                </h3>
                <div className="space-y-3">
                  {itemAnalysis.C.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="p-3 bg-green-50 rounded-lg border-l-4 border-green-600">
                      <p className="font-bold text-gray-800">{item.name}</p>
                      <div className="flex justify-between mt-1">
                        <span className="text-sm text-gray-600">Qty: {item.quantity}</span>
                        <span className="text-sm font-bold text-green-600">₹{item.revenue.toFixed(2)}</span>
                      </div>
                      <div className="w-full bg-green-200 rounded-full h-2 mt-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full" 
                          style={{ width: `${Math.min(item.percentage, 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{item.percentage}% of total revenue</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Detailed Item Table */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">📋 All Items - ABC Classification</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-orange-100 to-amber-100 border-b-2 border-orange-300">
                      <th className="px-4 py-3 text-left text-gray-800 font-bold">Item Name</th>
                      <th className="px-4 py-3 text-center text-gray-800 font-bold">Category</th>
                      <th className="px-4 py-3 text-right text-gray-800 font-bold">Qty Sold</th>
                      <th className="px-4 py-3 text-right text-gray-800 font-bold">Total Revenue</th>
                      <th className="px-4 py-3 text-right text-gray-800 font-bold">% of Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemAnalysis.all.slice(0, 15).map((item, idx) => (
                      <tr key={idx} className="border-b hover:bg-orange-50 transition">
                        <td className="px-4 py-3 text-gray-800 font-semibold">{item.name}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-3 py-1 rounded-full text-white font-bold text-xs ${
                            item.classification === 'A' ? 'bg-orange-600' :
                            item.classification === 'B' ? 'bg-blue-600' :
                            'bg-green-600'
                          }`}>
                            {item.classification}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700">{item.quantity}</td>
                        <td className="px-4 py-3 text-right text-gray-800 font-bold">₹{item.revenue.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right text-gray-700">{item.percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ABC Analysis Guide */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl shadow-lg p-8 border-l-4 border-blue-600">
              <h3 className="text-xl font-bold text-blue-900 mb-4">📚 ABC Analysis Guide</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h4 className="font-bold text-orange-600 mb-2">Category A - Fast Moving 🔥</h4>
                  <p className="text-sm text-gray-700">
                    High-revenue items contributing to 80% of total revenue. Focus on maintaining stock and quality.
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-blue-600 mb-2">Category B - Moderate 📊</h4>
                  <p className="text-sm text-gray-700">
                    Medium-revenue items (80-95% cumulative). Maintain balanced inventory and monitor trends.
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-green-600 mb-2">Category C - Slow Moving 🐢</h4>
                  <p className="text-sm text-gray-700">
                    Low-revenue items (95-100% cumulative). Consider promotions, bundling, or discontinuing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PROFIT & LOSS REPORT */}
        {reportType === 'profitloss' && profitLossData && (
          <div className="space-y-6">
            {/* Cost Calculation Info Banner */}
            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Inventory Overview</h3>
                  <div className="text-sm text-blue-700 mt-2 space-y-1">
                    <p>📦 <strong>{profitLossData.totalItems} total items</strong> in inventory</p>
                    <p>✅ <strong>{profitLossData.soldItems} items sold</strong> (generating revenue)</p>
                    <p>⏸️ <strong>{profitLossData.unsoldItems} items not sold</strong> (current stock: 0 sales)</p>
                  </div>
                  <div className="text-sm text-blue-700 mt-3 space-y-1">
                    <p className="font-semibold">Cost Calculation Methods:</p>
                    <p>✓ <strong>{profitLossData.itemsWithActualCost} items</strong> using actual cost_per_unit</p>
                    <p>📊 <strong>{profitLossData.itemsWithEstimatedCost} items</strong> using estimated cost (35%)</p>
                    {profitLossData.itemsWithNoCost > 0 && (
                      <p className="text-orange-600">⚠️ <strong>{profitLossData.itemsWithNoCost} items</strong> with no cost data</p>
                    )}
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    For more accurate results, add cost_per_unit to inventory items.
                  </p>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-green-600">
                <p className="text-gray-600 text-xs font-medium">Total Revenue</p>
                <p className="text-2xl font-bold text-green-600 mt-2">₹{profitLossData.totalRevenue.toFixed(2)}</p>
                <p className="text-xs text-gray-500 mt-1">Sales income</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-red-600">
                <p className="text-gray-600 text-xs font-medium">Total Cost</p>
                <p className="text-2xl font-bold text-red-600 mt-2">₹{profitLossData.totalCost.toFixed(2)}</p>
                <p className="text-xs text-gray-500 mt-1">Inventory costs</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-blue-600">
                <p className="text-gray-600 text-xs font-medium">Net Profit</p>
                <p className={`text-2xl font-bold mt-2 ${profitLossData.totalProfit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  ₹{profitLossData.totalProfit.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Revenue - Cost</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-purple-600">
                <p className="text-gray-600 text-xs font-medium">Profit Margin</p>
                <p className={`text-2xl font-bold mt-2 ${profitLossData.profitMargin >= 0 ? 'text-purple-600' : 'text-red-600'}`}>
                  {profitLossData.profitMargin}%
                </p>
                <p className="text-xs text-gray-500 mt-1">Net profit %</p>
              </div>
            </div>

            {/* Unsold Inventory Card */}
            <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-orange-600">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-xs font-medium">Unsold Inventory Cost</p>
                  <p className="text-2xl font-bold text-orange-600 mt-2">₹{profitLossData.totalUnsoldCost.toFixed(2)}</p>
                  <p className="text-xs text-gray-500 mt-1">Capital tied up in stock</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-700">{profitLossData.unsoldItems} items</p>
                  <p className="text-xs text-gray-500">not yet sold</p>
                </div>
              </div>
            </div>

            {/* Profit Distribution Chart */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Profit Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Net Profit', value: profitLossData.totalProfit, fill: '#2563eb' },
                      { name: 'Total Cost', value: profitLossData.totalCost, fill: '#dc2626' }
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ₹${value.toFixed(2)}`}
                    outerRadius={100}
                    dataKey="value"
                  >
                  </Pie>
                  <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Top 10 Most Profitable Items */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">💰 Top 10 Most Profitable Items</h3>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={profitLossData.topProfitable}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                  <Legend />
                  <Bar dataKey="revenue" fill="#10b981" name="Revenue" />
                  <Bar dataKey="cost" fill="#ef4444" name="Cost" />
                  <Bar dataKey="profit" fill="#3b82f6" name="Profit" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Least Profitable Items */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">⚠️ 10 Least Profitable Items</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left p-3 font-semibold text-gray-700">Item Name</th>
                      <th className="text-center p-3 font-semibold text-gray-700">Qty Sold</th>
                      <th className="text-right p-3 font-semibold text-gray-700">Revenue</th>
                      <th className="text-right p-3 font-semibold text-gray-700">Cost</th>
                      <th className="text-right p-3 font-semibold text-gray-700">Profit</th>
                      <th className="text-right p-3 font-semibold text-gray-700">Margin %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profitLossData.leastProfitable.map((item, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="p-3 font-medium text-gray-800">{item.name}</td>
                        <td className="p-3 text-center text-gray-700">{item.quantitySold}</td>
                        <td className="p-3 text-right text-green-600 font-semibold">₹{item.revenue.toFixed(2)}</td>
                        <td className="p-3 text-right text-red-600 font-semibold">₹{item.cost.toFixed(2)}</td>
                        <td className={`p-3 text-right font-bold ${item.profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                          ₹{item.profit.toFixed(2)}
                        </td>
                        <td className={`p-3 text-right font-bold ${item.margin >= 0 ? 'text-purple-600' : 'text-red-600'}`}>
                          {item.margin}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Complete Item Breakdown */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">📊 Complete Item Breakdown (All {profitLossData.totalItems} Items)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left p-3 font-semibold text-gray-700">Item Name</th>
                      <th className="text-center p-3 font-semibold text-gray-700">Current Stock</th>
                      <th className="text-center p-3 font-semibold text-gray-700">Qty Sold</th>
                      <th className="text-right p-3 font-semibold text-gray-700">Revenue</th>
                      <th className="text-right p-3 font-semibold text-gray-700">Cost</th>
                      <th className="text-right p-3 font-semibold text-orange-700">Unsold Cost</th>
                      <th className="text-right p-3 font-semibold text-gray-700">Profit</th>
                      <th className="text-right p-3 font-semibold text-gray-700">Margin %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profitLossData.itemBreakdown.map((item, idx) => (
                      <tr key={idx} className={`border-b border-gray-100 hover:bg-gray-50 ${
                        item.quantitySold === 0 ? 'bg-gray-100' : 
                        item.calculationMethod === 'estimated' ? 'bg-blue-50' : 
                        item.calculationMethod === 'none' ? 'bg-yellow-50' : ''
                      }`}>
                        <td className="p-3 font-medium text-gray-800">
                          {item.name}
                          {item.quantitySold === 0 && (
                            <span className="ml-2 text-xs bg-gray-300 text-gray-700 px-2 py-0.5 rounded">Not Sold</span>
                          )}
                          {item.quantitySold > 0 && item.calculationMethod === 'estimated' && (
                            <span className="ml-2 text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded" title="Cost estimated at 35% of selling price">Estimated</span>
                          )}
                          {item.quantitySold > 0 && item.calculationMethod === 'none' && (
                            <span className="ml-2 text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded">No Cost Data</span>
                          )}
                          {item.quantitySold > 0 && item.calculationMethod === 'cost_per_unit' && (
                            <span className="ml-2 text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded" title="Using actual cost from inventory">Actual Cost</span>
                          )}
                        </td>
                        <td className="p-3 text-center text-gray-700 font-semibold">{item.currentStock}</td>
                        <td className="p-3 text-center text-gray-700">
                          {item.quantitySold > 0 ? item.quantitySold : '-'}
                        </td>
                        <td className="p-3 text-right text-green-600 font-semibold">
                          {item.revenue > 0 ? `₹${item.revenue.toFixed(2)}` : '-'}
                        </td>
                        <td className="p-3 text-right text-red-600 font-semibold">
                          {item.hasCostData && item.quantitySold > 0 ? `₹${item.cost.toFixed(2)}` : '-'}
                        </td>
                        <td className="p-3 text-right text-orange-600 font-semibold">
                          {item.unsoldCost > 0 ? `₹${item.unsoldCost.toFixed(2)}` : '-'}
                        </td>
                        <td className={`p-3 text-right font-bold ${item.profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                          {item.hasCostData && item.quantitySold > 0 ? `₹${item.profit.toFixed(2)}` : '-'}
                        </td>
                        <td className={`p-3 text-right font-bold ${item.margin >= 0 ? 'text-purple-600' : 'text-red-600'}`}>
                          {item.hasCostData && item.quantitySold > 0 ? `${item.margin}%` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Profit & Loss Guide */}
            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl shadow-lg p-8 border-l-4 border-green-600">
              <h3 className="text-xl font-bold text-green-900 mb-4">💡 Profit & Loss Insights</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h4 className="font-bold text-green-600 mb-2">Revenue 💰</h4>
                  <p className="text-sm text-gray-700">
                    Total income from all sales. Higher revenue indicates more business activity.
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-red-600 mb-2">Cost 📉</h4>
                  <p className="text-sm text-gray-700">
                    Total cost of goods sold (COGS) based on inventory cost per unit. Manage costs to improve margins.
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-blue-600 mb-2">Profit Margin 📊</h4>
                  <p className="text-sm text-gray-700">
                    Percentage of profit relative to revenue. Higher margins indicate better profitability.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
